
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import { Heart, Eye, X } from "lucide-react";
import { useWishlistStore, useAuthStore } from "@/lib/store";
import { wishlistApi } from "@/lib/api";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";
import toast from "react-hot-toast";
import { emptyStates } from "@/lib/placeholders";
import { getSmartProductPlaceholder } from "@/lib/placeholders";

interface WishlistItem {
  id: number;
  product_id: number;
  product_name: string;
  product_slug: string;
  product_price: number;
  product_original_price: number | null;
  product_image_url: string | null;
  product_stock: number;
  created_at: string;
}

export default function WishlistPage() {
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const { token, isAuthenticated } = useAuthStore();
  const { removeItem: removeFromLocalWishlist, isInWishlist } = useWishlistStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  const fetchWishlist = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await wishlistApi.getAll(token);
      // Backend wraps response in: { success, message, data: { items, total } }
      const wrappedResponse = response;
      const data = wrappedResponse.data || wrappedResponse;
      const items = data.items || [];
      
      // Limit to 50 items
      const limitedItems = items.slice(0, 50);
      setWishlistItems(limitedItems);
    } catch (error: any) {
      console.error("Failed to fetch wishlist:", error);
      if (error.response?.status === 401) {
        toast.error("Please log in to view your wishlist");
      } else {
        toast.error("Failed to load wishlist");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!token) {
      toast.error("Please log in to remove items from wishlist");
      return;
    }

    try {
      const response = await wishlistApi.remove(productId, token);
      // Backend wraps response in: { success, message, data: { is_in_wishlist, product_id } }
      const wrappedResponse = response;
      const data = wrappedResponse.data || wrappedResponse;
      
      if (!data.is_in_wishlist) {
        // Remove from local state
        setWishlistItems((prev) => prev.filter((item) => item.product_id !== productId));
        // Remove from local store
        removeFromLocalWishlist(productId);
        toast.success("Removed from wishlist");
      }
    } catch (error: any) {
      console.error("Failed to remove from wishlist:", error);
      toast.error("Failed to remove item from wishlist");
    }
  };

  const calculateDaysInWishlist = (createdAt: string): string => {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - createdDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears >= 1) {
      return "More than a year";
    } else if (diffMonths >= 12) {
      return "More than a year";
    } else if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "1 day";
    } else if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffMonths === 1) {
      return "1 month";
    } else {
      return `${diffMonths} months`;
    }
  };

  const calculateDiscount = (price: number, originalPrice: number | null) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  const handleOTPSuccess = () => {
    setShowOTPDialog(false);
    // Wishlist will be fetched automatically via useEffect when isAuthenticated becomes true
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col">
        <OTPLoginDialog 
          isOpen={showOTPDialog} 
          onClose={() => setShowOTPDialog(false)} 
          onSuccess={handleOTPSuccess} 
        />
        <main className="flex-grow pt-24 pb-24">
          <div className="container mx-auto px-4 py-12">
            <div className="text-center py-20">
              <Heart className="w-24 h-24 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">Please log in to view your wishlist</h2>
              <p className="text-gray-600 mb-6">Sign in to see your saved items</p>
              <button
                onClick={() => setShowOTPDialog(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-aarsa-gold-500 to-aarsa-gold-600 text-white rounded-lg hover:from-aarsa-gold-600 hover:to-aarsa-gold-700 transition-all"
              >
                <span>Log In</span>
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow pt-24 pb-24">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl font-bold font-playfair">My Wishlist</h1>
            {!loading && wishlistItems.length > 0 && (
              <span className="text-sm text-gray-600">
                {wishlistItems.length} {wishlistItems.length === 1 ? "item" : "items"}
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className="group relative">
                  <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-1/2 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : wishlistItems.length === 0 ? (
            <div className="text-center py-20">
              <Heart className="w-24 h-24 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">{emptyStates.wishlist.title}</h2>
              <p className="text-gray-600 mb-6">{emptyStates.wishlist.description}</p>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-aarsa-gold-500 to-aarsa-gold-600 text-white rounded-lg hover:from-aarsa-gold-600 hover:to-aarsa-gold-700 transition-all"
              >
                <span>Start Shopping</span>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
              {wishlistItems.map((item, index) => {
                const discount = calculateDiscount(item.product_price, item.product_original_price);
                const imageUrl = item.product_image_url || getSmartProductPlaceholder(item.product_name);
                const daysInWishlist = calculateDaysInWishlist(item.created_at);

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                    className="group relative"
                  >
                    <Link to={`/products/${item.product_slug}`}>
                      <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                        {/* Product Image */}
                        <img
                          src={imageUrl}
                          alt={item.product_name}
                          className={`object-cover w-full h-full transition-opacity duration-300 ${
                            item.product_stock === 0 ? "opacity-50" : "opacity-100"
                          }`}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = getSmartProductPlaceholder(item.product_name);
                          }}
                        />

                        {/* Sold Out Badge - Top Right */}
                        {item.product_stock === 0 && (
                          <div className="absolute top-2 right-2 z-20">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-black text-white shadow-sm">
                              Sold out
                            </span>
                          </div>
                        )}

                        {/* Days in Wishlist Badge */}
                        {item.product_stock > 0 && (
                          <div className="absolute top-2 left-2 z-10">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-purple-500/90 backdrop-blur-sm text-white shadow-sm">
                              {daysInWishlist}
                            </span>
                          </div>
                        )}

                        {/* Discount Badge */}
                        {discount > 0 && item.product_stock > 0 && (
                          <div className="absolute top-2 right-2 z-10">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-red-500 text-white shadow-sm">
                              {discount}% OFF
                            </span>
                          </div>
                        )}

                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex space-x-4">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                              aria-label="Quick view"
                            >
                              <Eye className="w-5 h-5 text-gray-900" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => handleRemoveFromWishlist(item.product_id, e)}
                              className="p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors text-red-500"
                              aria-label="Remove from wishlist"
                            >
                              <X className="w-5 h-5" />
                            </motion.button>
                          </div>
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="mt-4 space-y-2">
                        <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                          {item.product_name}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className="text-xl font-bold text-gray-900">
                            ₹{item.product_price.toLocaleString()}
                          </span>
                          {item.product_original_price && discount > 0 && (
                            <>
                              <span className="text-sm text-gray-500 line-through">
                                ₹{item.product_original_price.toLocaleString()}
                              </span>
                              <span className="text-sm font-semibold text-green-600">
                                Save {discount}%
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
